import React, { useState, useEffect } from "react";
import { Order } from "../../interfaces/orders_manage";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../state/store";
import Modal from "react-modal";
import {
  createDelivery,
  updateDelivery,
} from "../../state/slices/deliverySlice";
import "./DeliveryModal.css";

Modal.setAppElement("#root");

interface Props {
  order: Order | null;
  onClose: () => void;
  onSuccess: () => void;
  isOpen: boolean;
  deliveries?: any[];
  loading?: boolean;
  error?: string | null;
}

const DeliveryModal: React.FC<Props> = ({
  order,
  onClose,
  onSuccess,
  isOpen,
  deliveries = [],
  loading,
  error,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [status, setStatus] = useState("pending");
  const [location, setLocation] = useState("");
  const [trackId, setTrackId] = useState(""); // ✅ new state

  // Find existing delivery for this order
  const existingDelivery = deliveries.find(
    (d) => d.orderId === order?.id
  );

  // Initialize form fields when modal opens or delivery changes
  useEffect(() => {
    if (isOpen) {
      if (existingDelivery) {
        setStatus(existingDelivery.status);
        setLocation(existingDelivery.location || "");
        setTrackId(existingDelivery.trackId || ""); // ✅ preload trackId if editing
      } else {
        setStatus("pending");
        setLocation("");
        setTrackId(""); // ✅ reset
      }
    }
  }, [isOpen, existingDelivery]);

  const handleSubmit = async () => {
    if (!order) return;

    try {
      if (existingDelivery) {
        await dispatch(
          updateDelivery({
            id: existingDelivery.id,
            status,
            location: location || undefined,
            trackId: trackId || undefined, // ✅ pass trackId
          })
        ).unwrap();
        alert("Updated successfully");
      } else {
        await dispatch(
          createDelivery({
            orderId: order.id,
            status,
            location: location || undefined,
            trackId: trackId || undefined, // ✅ pass trackId
          })
        ).unwrap();
        alert("Added successfully");
      }
      onSuccess();
    } catch (err) {
      alert("Error saving delivery: " + err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Delivery Modal"
      className="deliveryModal_content"
      overlayClassName="deliveryModal_overlay"
    >
      <h2>
        {existingDelivery
          ? "Update Delivery"
          : order
          ? `Tracking Order ${order.id?.substring(0, 8)}...`
          : "Delivery"}
      </h2>

      {/* ✅ Track ID input */}
      <label>Track Id</label>
      <input
        type="text"
        className="deliveryModal_content"
        value={trackId}
        onChange={(e) => setTrackId(e.target.value)}
        placeholder="Tracking Id (optional)"
        disabled={loading}
      />

      <label>Status</label>
      <select
        className="deliveryModal_content"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        disabled={loading}
      >
        <option value="pending">Pending</option>
        <option value="processing">Processing</option>
        <option value="paid">Paid</option>
        <option value="shipped">Shipped</option>
        <option value="delivered">Delivered</option>
        <option value="canceled">Canceled</option>
      </select>


      <label>Location (optional)</label>
      <input
        type="text"
        className="deliveryModal_content"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        placeholder="City, address, etc."
        disabled={loading}
      />

      <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
        <button
          onClick={handleSubmit}
          className="deliveryModal_btn_primary"
          disabled={loading}
        >
          Save
        </button>
        <button
          onClick={onClose}
          className="deliveryModal_btn_secondary"
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default DeliveryModal;
